import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Container,
  Stack,
  InputAdornment
} from '@mui/material'
import { Pool as PoolIcon, Save as SaveIcon } from '@mui/icons-material'

const PoolConfig = ({ pool, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    volume_gallons: '',
    pool_type: 'vinyl_liner',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (pool) {
      setFormData({
        name: pool.name,
        volume_gallons: pool.volume_gallons,
        pool_type: pool.pool_type,
      })
    }
  }, [pool])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'volume_gallons' ? parseFloat(value) || '' : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const result = await onSave(formData)
      if (result.error) {
        setError(result.error.message)
      } else {
        onCancel()
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PoolIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {pool ? 'Edit Pool' : 'Create New Pool'}
          </Typography>
        </Box>
        
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              name="name"
              label="Pool Name"
              placeholder="e.g., Backyard Pool"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PoolIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }
              }}
            />

            <TextField
              name="volume_gallons"
              label="Volume"
              placeholder="e.g., 15000"
              type="number"
              value={formData.volume_gallons}
              onChange={handleChange}
              required
              fullWidth
              variant="outlined"
              inputProps={{
                min: 100,
                max: 50000,
                step: 100
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    gallons
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }
              }}
            />

            <FormControl 
              fullWidth 
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }
              }}
            >
              <InputLabel>Pool Type</InputLabel>
              <Select
                name="pool_type"
                value={formData.pool_type}
                onChange={handleChange}
                label="Pool Type"
              >
                <MenuItem value="vinyl_liner">Vinyl Liner</MenuItem>
                <MenuItem value="plaster">Plaster</MenuItem>
                <MenuItem value="fiberglass">Fiberglass</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                fullWidth
                size="large"
                sx={{ 
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1.1rem'
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                fullWidth
                size="large"
                startIcon={loading ? null : <SaveIcon />}
                sx={{ 
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                }}
              >
                {loading ? 'Saving...' : 'Save Pool'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Container>
  )
}

export default PoolConfig